import React, { useCallback, useEffect, useRef, useState } from 'react';

const SPEEDS = [1, 0.5, 0.25, 2];

const fmt = (seconds: number): string => {
  if (!Number.isFinite(seconds)) {
    return '0:00';
  }
  const total = Math.max(0, Math.floor(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

interface Props {
  src: string;
  poster?: string;
  caption?: string;
}

/**
 * Video figure with controls built to match the reading page rather than the
 * browser's chrome. The clips here are silent screen recordings, so there is no
 * volume control; speed matters more, since they run sped up.
 */
const VideoFigure: React.FC<Props> = ({ src, poster, caption }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speedIndex, setSpeedIndex] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [scrubbing, setScrubbing] = useState(false);

  const speed = SPEEDS[speedIndex];

  const toggle = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      setStarted(true);
      void video.play();
    } else {
      video.pause();
    }
  }, []);

  const seekBy = useCallback((delta: number) => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration)) return;
    video.currentTime = Math.min(
      video.duration,
      Math.max(0, video.currentTime + delta)
    );
  }, []);

  const cycleSpeed = useCallback(() => {
    setSpeedIndex((current) => (current + 1) % SPEEDS.length);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void wrapRef.current?.requestFullscreen?.();
    }
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.playbackRate = speed;
    }
  }, [speed]);

  useEffect(() => {
    const onChange = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // Scoped to the figure so page-level scrolling with the space bar is only
  // captured once the reader has focused this player.
  const onKeyDown = (event: React.KeyboardEvent) => {
    // Space on a focused button belongs to that button, not to playback.
    const onButton = (event.target as HTMLElement).tagName === 'BUTTON';

    switch (event.key) {
      case ' ':
        if (onButton) return;
        event.preventDefault();
        toggle();
        break;
      case 'k':
        event.preventDefault();
        toggle();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        seekBy(-5);
        break;
      case 'ArrowRight':
        event.preventDefault();
        seekBy(5);
        break;
      case 'f':
        event.preventDefault();
        toggleFullscreen();
        break;
      default:
        break;
    }
  };

  const progress = duration ? (time / duration) * 100 : 0;

  return (
    <figure className="reading-page__video">
      <div
        ref={wrapRef}
        className={`video-figure${started ? ' video-figure--started' : ''}${
          playing ? ' video-figure--playing' : ''
        }`}
        tabIndex={0}
        role="group"
        aria-label="Video player. Space to play or pause, left and right arrows to seek, F for full screen."
        onKeyDown={onKeyDown}
      >
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          loop
          muted
          playsInline
          preload="metadata"
          onClick={() => {
            // Clicking the picture should also hand it the keyboard.
            wrapRef.current?.focus();
            toggle();
          }}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
          onTimeUpdate={(e) => {
            if (!scrubbing) setTime(e.currentTarget.currentTime);
          }}
        />

        {!started && (
          <button
            type="button"
            className="video-figure__cover"
            onClick={() => {
              wrapRef.current?.focus();
              toggle();
            }}
            aria-label="Play video"
          >
            <span className="video-figure__cover-mark" aria-hidden="true">
              ▶
            </span>
          </button>
        )}

        <div className="video-figure__bar">
          <button
            type="button"
            className="video-figure__btn"
            onClick={toggle}
            aria-label={playing ? 'Pause' : 'Play'}
          >
            {playing ? '❚❚' : '▶'}
          </button>

          <input
            className="video-figure__scrub"
            type="range"
            min={0}
            max={duration || 0}
            step={0.05}
            value={time}
            style={{ ['--progress' as string]: `${progress}%` }}
            aria-label="Seek"
            aria-valuetext={`${fmt(time)} of ${fmt(duration)}`}
            onPointerDown={() => setScrubbing(true)}
            onPointerUp={() => setScrubbing(false)}
            onChange={(e) => {
              const next = Number(e.target.value);
              setTime(next);
              if (videoRef.current) {
                videoRef.current.currentTime = next;
              }
            }}
          />

          <span className="video-figure__time">
            {fmt(time)} / {fmt(duration)}
          </span>

          <button
            type="button"
            className="video-figure__btn video-figure__btn--speed"
            onClick={cycleSpeed}
            aria-label={`Playback speed: ${speed} times. Click to change.`}
          >
            {speed}&times;
          </button>

          <button
            type="button"
            className="video-figure__btn"
            onClick={toggleFullscreen}
            aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
          >
            {fullscreen ? '⤡' : '⤢'}
          </button>
        </div>
      </div>

      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
};

export default VideoFigure;
